import { CloseOutlined, ViewList } from "@mui/icons-material";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Box,
    Divider,
    CircularProgress,
    Grid,
    Paper,
    Chip,
    Stack,
    TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { useGetPaymentHistoryListQuery } from "../../store/common/commonApis";
import { capitalizeFirstLetter, handleDateTimeDefault, JsonDecode } from "../../helpers/utils";
import { EnrollActivityAddDrawer } from "../admin/enroll-activity-manager/EnrollActivityAddDrawer";
import { useGetBookingListQuery } from "../../store/booking/bookingApis";
import Button from "./Button";
import { getType } from "@reduxjs/toolkit";
import { getCookie, isAuth } from "../../helpers/cookies";
import { baseUrl } from "../../helpers/constants";
import { format } from "date-fns";
import Input from "./Input";
import { Fragment } from "react";

export const MemberHistoryModal = ({ show, close, getActivityList, getMembersList }) => {
    const [showDrawer, setShowDrawer] = React.useState(false);
    const [bookingInitalValue, setBookingInitalValue] = React.useState(false);
    const [bookingId, setBookingId] = React.useState(null);

    const [editingIndex, setEditingIndex] = React.useState(null);
    const [differenceAmount, setEditDifferenceAmount] = React.useState(0);
    const [yearFilter, setYearFilter] = React.useState(new Date().getFullYear());
    const [paymentTypeFilter, setPaymentTypeFilter] = React.useState("All");
    const [filteredData, setFilteredData] = React.useState([]);
    const [editedRemark, setEditedRemark] = React.useState("");
    const [bookingType, setBookingType] = React.useState("enrollment");

    const { data: bookings, isLoading: bookingsLoading } = 
    useGetBookingListQuery(
        {
            pageNo: 0,
            limit: 1,
            sortBy: -1,
            booking_id: bookingId,
            type: bookingType,
        },
        {
            skip: !bookingId,
        },
    );

    useEffect(() => {
        if (bookings && bookings?.result && bookings?.result.length > 0) {
            setBookingInitalValue(bookings?.result[0]);
            setShowDrawer(true);
        }
    }, [bookings]);

    const {
        isLoading,
        data: paymentHistory,
        refetch,
        isFetching,
    } = useGetPaymentHistoryListQuery({
        pageNo: 0,
        limit: 100,
        sortBy: -1,
        sortField: "createdAt",
        keywords: show,
        filter_by: "member_id",
        payment_status: "Success",
    });

    const years = [2024, 2025, 2026];

    const isTransferEnrollmentPayment = (item) => {
        return (
            item?.booking_type === "enrollment" &&
            String(item?.payment_mode || "").toLowerCase() === "transfer" &&
            String(item?.remarks || "")
                .toLowerCase()
                .includes("amount transferred from previous enrollment")
        );
    };

    const getPaymentBookingIds = (item) => {
        const ids = Array.isArray(item?.booking_id)
            ? item.booking_id
            : item?.booking_id
            ? [item.booking_id]
            : [];

        return ids.filter(Boolean).map((id) => String(id));
    };

    const paymentsBelongToSameBooking = (item1, item2) => {
        const ids1 = getPaymentBookingIds(item1);
        const ids2 = getPaymentBookingIds(item2);

        return ids1.some((id) => ids2.includes(id));
    };

    const getActualPaidAmount = (item) => {
        return (
            Number(item?.amount_paid || 0) +
            Number(item?.difference_amount_paid || 0)
        );
    };

    const combineTransferPayments = (data) => {
        const groupedPayments = new Map();
        const consumedIndexes = new Set();

        // Find all transfer payments
        const transferPayments = data
            .map((payment, index) => ({ payment, index }))
            .filter(({ payment }) => isTransferEnrollmentPayment(payment));

        transferPayments.forEach(({ payment: transferPayment, index: transferIndex }) => {
            // Already included in another combined entry
            if (consumedIndexes.has(transferIndex)) {
                return;
            }

            // ---------------------------------------------------------
            // Find the complete connected payment group.
            //
            // Example:
            // Transfer #576816
            //        ↓
            // Online payment contains #576816 + #576817
            //        ↓
            // Transfer #576817
            //
            // Therefore all 3 payments belong to ONE history entry.
            // ---------------------------------------------------------
            const groupIndexes = new Set([transferIndex]);

            let changed = true;

            while (changed) {
                changed = false;

                data.forEach((payment, paymentIndex) => {
                    if (groupIndexes.has(paymentIndex)) {
                        return;
                    }

                    if (isRenewalPayment(payment)) {
                        return;
                    }

                    for (const groupIndex of groupIndexes) {
                        const groupPayment = data[groupIndex];

                        if (
                            paymentsBelongToSameBooking(
                                groupPayment,
                                payment
                            )
                        ) {
                            groupIndexes.add(paymentIndex);
                            changed = true;
                            break;
                        }
                    }
                });
            }

            const payments = Array.from(groupIndexes)
                .map((paymentIndex) => ({
                    payment: data[paymentIndex],
                    paymentIndex,
                }))
                .filter(({ payment }) => !isRenewalPayment(payment))
                .sort(
                    (a, b) =>
                        new Date(a.payment?.createdAt) -
                        new Date(b.payment?.createdAt)
                );

            if (payments.length <= 1) {
                return;
            }

            // ---------------------------------------------------------
            // MERGE ALL BOOKINGS
            // ---------------------------------------------------------
            const mergedBookings = [];
            const bookingKeys = new Set();

            payments.forEach(({ payment }) => {
                (payment?.bookings_data || []).forEach((booking) => {
                    const key =
                        booking?._id ||
                        booking?.booking_id ||
                        JSON.stringify(booking);

                    if (!bookingKeys.has(String(key))) {
                        bookingKeys.add(String(key));
                        mergedBookings.push(booking);
                    }
                });
            });

            // ---------------------------------------------------------
            // MERGE PLAN DATA
            // ---------------------------------------------------------
            const mergedPlans = [];
            const planKeys = new Set();

            payments.forEach(({ payment }) => {
                (payment?.plans_data || []).forEach((plan) => {
                    const key =
                        plan?._id ||
                        plan?.plan_id ||
                        JSON.stringify(plan);

                    if (!planKeys.has(String(key))) {
                        planKeys.add(String(key));
                        mergedPlans.push(plan);
                    }
                });
            });

            // ---------------------------------------------------------
            // MERGE ACTIVITY DATA
            // ---------------------------------------------------------
            const mergedActivities = [];
            const activityKeys = new Set();

            payments.forEach(({ payment }) => {
                (payment?.activity_data || []).forEach((activity) => {
                    const key =
                        activity?._id ||
                        activity?.activity_id ||
                        JSON.stringify(activity);

                    if (!activityKeys.has(String(key))) {
                        activityKeys.add(String(key));
                        mergedActivities.push(activity);
                    }
                });
            });

            // ---------------------------------------------------------
            // CALCULATE TOTAL PAYMENT
            // ---------------------------------------------------------
            const totalPaid = payments.reduce(
                (total, { payment }) =>
                    total + getActualPaidAmount(payment),
                0
            );

            // ---------------------------------------------------------
            // PAYMENT BREAKDOWN
            // Aggregate same payment modes into ONE line.
            // ---------------------------------------------------------
            let transferAmount = 0;
            let walletAmount = 0;
            let onlineAmount = 0;

            payments.forEach(({ payment }) => {
                const totalPaymentAmount =
                    getActualPaidAmount(payment);

                const walletUsed = Number(
                    payment?.wallet_amount_used || 0
                );

                // Transfer
                if (isTransferEnrollmentPayment(payment)) {
                    transferAmount += totalPaymentAmount;
                    return;
                }

                // Wallet
                walletAmount += walletUsed;

                // Online
                onlineAmount += Math.max(
                    totalPaymentAmount - walletUsed,
                    0
                );
            });

            const paymentParts = [];

            if (transferAmount > 0) {
                paymentParts.push({
                    amount: transferAmount,
                    mode: "Transfer",
                });
            }

            if (walletAmount > 0) {
                paymentParts.push({
                    amount: walletAmount,
                    mode: "Wallet",
                });
            }

            if (onlineAmount > 0) {
                const onlinePayment = payments.find(
                    ({ payment }) =>
                        !isTransferEnrollmentPayment(payment) &&
                        Math.max(
                            getActualPaidAmount(payment) -
                                Number(payment?.wallet_amount_used || 0),
                            0
                        ) > 0
                )?.payment;

                paymentParts.push({
                    amount: onlineAmount,
                    mode: onlinePayment?.payment_mode || "Online",
                    order_id: onlinePayment?.order_id || "-",
                    payment_response:
                        onlinePayment?.payment_response || "",
                    createdAt: onlinePayment?.createdAt,
                });
            }

            // ---------------------------------------------------------
            // USE ONLINE PAYMENT AS MAIN HISTORY RECORD
            // because it contains the actual gateway transaction details.
            // ---------------------------------------------------------
            const onlinePayment = payments.find(
                ({ payment }) =>
                    !isTransferEnrollmentPayment(payment)
            )?.payment;

            const combinedItem = {
                ...onlinePayment,

                is_transfer_combined: true,

                combined_total_paid: totalPaid,

                combined_payment_parts: paymentParts,

                // IMPORTANT:
                // Show BOTH activities/bookings in ONE history card.
                bookings_data: mergedBookings,

                plans_data: mergedPlans,

                activity_data: mergedActivities,

                // Keep latest payment date
                createdAt:
                    payments[payments.length - 1]?.payment?.createdAt ||
                    onlinePayment?.createdAt,

                // Keep gateway information
                order_id:
                    onlinePayment?.order_id ||
                    "-",

                payment_response:
                    onlinePayment?.payment_response ||
                    "",

                remarks:
                    "Combined payment for multiple enrollments",
            };

            // Use the first transfer index as the location of
            // the combined history item.
            groupedPayments.set(
                transferIndex,
                combinedItem
            );

            // Consume ALL payments in this group
            groupIndexes.forEach((paymentIndex) => {
                consumedIndexes.add(paymentIndex);
            });
        });

        return data
            .map((item, index) => {
                // Return ONE combined entry
                if (groupedPayments.has(index)) {
                    return groupedPayments.get(index);
                }

                // Already included inside the combined entry
                if (consumedIndexes.has(index)) {
                    return null;
                }

                // Existing behavior for standalone transfer payment
                if (isTransferEnrollmentPayment(item)) {
                    return null;
                }

                return item;
            })
            .filter(Boolean);
    };

    useEffect(() => {
        if (paymentHistory?.result?.length > 0) {
            let data = paymentHistory.result;

            // Filter by year
            data = data.filter((item) =>
                String(item?.createdAt || "").includes(String(yearFilter))
            );

            // Filter by payment type
            if (paymentTypeFilter !== "All") {
                data = data.filter(
                    (item) => getTypeLabel(item) === paymentTypeFilter
                );
            }

            setFilteredData(combineTransferPayments(data));
        } else {
            setFilteredData([]);
        }
    }, [paymentHistory, yearFilter, paymentTypeFilter]);

    const getTotalAmount = useCallback(
        (type) => {
            let total = 0;

            if (filteredData) {
                filteredData.forEach((obj) => {
                    if (type === "membership") {
                        total += getActualMembershipAmount(obj);
                    }
                    else if (type === "enrollment") {
                        const bookingsForAmount = isRenewalPayment(obj)
                            ? obj?.bookings_data?.filter(
                                (booking) => booking?.status === true
                            )
                            : obj?.bookings_data;

                        total += bookingsForAmount?.reduce((acc, cur) => {
                            return acc + Number(cur?.total_amount || 0);
                        }, 0);
                    }
                });
            }

            return total;
        },
        [filteredData]
    );

    const getSingleAmount = (obj, type) => {
        const membershipPlan = obj?.plans_data?.find(
            (plan) => plan?.plan_type === "membership"
        );

        const membershipAmount = Number(membershipPlan?.amount || 0);

        if (type === "membership") {
            return getActualMembershipAmount(obj);
        }

        if (type === "booking") {
            return Number(
                obj?.amount_paid ??
                obj?.bookings_data?.[0]?.total_amount ??
                obj?.bookings_data?.[0]?.fees_breakup?.price ??
                0
            );
        }

        if (type === "enrollment") {
            const hasMembership = obj?.plans_data?.some(
                (plan) => plan?.plan_type === "membership"
            );

            const hasEnrollment = obj?.plans_data?.some(
                (plan) => plan?.plan_type === "enrollment"
            );

            // Both Membership + Enrollment
            if (hasMembership && hasEnrollment) {
                return Math.max(
                    Number(obj?.amount_paid || 0) - membershipAmount,
                    0
                );
            }

            // Enrollment only
            if (hasEnrollment) {
                return Number(obj?.amount_paid || 0);
            }

            return 0;
        }



        return 0;
    };

    const getEnrollmentBookingAmount = (booking) => {
        return Number(
                booking?.total_amount ??
                booking?.fees_breakup?.amount ??
                booking?.fees_breakup?.price ??
                0
            );
        };

    const getItemEnrollmentAmount = (item) => {
        const bookings = getEnrollmentBookingsForDisplay(item);

        return bookings.reduce((total, booking) => {
            return total + Number(booking?.total_amount || 0);
        }, 0);
    };

    const getEnrollmentPaymentBreakdown = (item) => {
        const totalAmount = getItemEnrollmentAmount(item);

        const walletAmount = Number(item?.wallet_amount_used || 0);

        const onlineAmount = Math.max(totalAmount - walletAmount, 0);

        return {
            totalAmount,
            walletAmount,
            onlineAmount,
        };
    };

    const getBookingEventPaymentBreakdown = (item) => {
        const totalAmount = Number(item?.amount_paid || 0);
        const walletAmount = Number(item?.wallet_amount_used || 0);
        const onlineAmount = Math.max(totalAmount - walletAmount, 0);

        return {
            totalAmount,
            walletAmount,
            onlineAmount,
        };
    };

    const getMembershipPaymentBreakdown = (item) => {
        const totalAmount = getActualPaidAmount(item);
        const walletAmount = Number(item?.wallet_amount_used || 0);
        const onlineAmount = Math.max(totalAmount - walletAmount, 0);

        return {
            totalAmount,
            walletAmount,
            onlineAmount,
        };
    };

    const isRenewalPayment = (item) => {
        return (
            item?.booking_type === "renewal" ||
            item?.remarks?.startsWith("Renew Payment") ||
            item?.remarks?.toLowerCase()?.includes("bulk renewal payment")
        );
    };

    // New renewal created by the new logic:
    // payment history contains BOTH old booking ID and new booking ID.
    const isNewRenewalPayment = (item) => {
        return (
            isRenewalPayment(item) &&
            Array.isArray(item?.booking_id) &&
            item.booking_id.length > 1
        );
    };

    // Get the correct enrollment plan for payment history.
    //
    // OLD / LEGACY PAYMENT:
    // Use plans_data because the old booking may have been modified later.
    //
    // NEW RENEWAL:
    // Use the active booking's fees_breakup because the new renewal
    // creates a new booking with the new plan.
    const getEnrollmentPlanForDisplay = (item, booking) => {
        const enrollmentPlans = Array.isArray(item?.plans_data)
            ? item.plans_data.filter(
                (plan) => plan?.plan_type === "enrollment"
            )
            : [];

        // Old/original enrollment payment
        if (!isRenewalPayment(item)) {
            // This booking was reused by a later renewal.
            // Show the historical plan stored in the old payment.
            if (wasRenewedLater(item, booking)) {
                return enrollmentPlans[0] || {};
            }

            // Normal payment / combined payment:
            // use the plan belonging to the exact booking.
            return booking?.fees_breakup || enrollmentPlans[0] || {};
        }

        // New renewal payment
        if (isNewRenewalPayment(item)) {
            return (
                booking?.fees_breakup ||
                enrollmentPlans[enrollmentPlans.length - 1] ||
                {}
            );
        }

        // Legacy renewal payment
        // Old renewal logic reused the same booking, so use the
        // historical plan stored inside payment history.
        return (
            enrollmentPlans[0] ||
            booking?.fees_breakup ||
            {}
        );
    };

    // Check whether this booking was renewed again later.
    //
    // This is mainly for OLD payment records where the old renewal
    // logic reused the same booking.
    const wasRenewedLater = (item, booking) => {
        if (!booking?._id) {
            return false;
        }

        const currentPaymentDate = new Date(item?.createdAt);

        if (Number.isNaN(currentPaymentDate.getTime())) {
            return false;
        }

        const allPayments = paymentHistory?.result || [];

        return allPayments.some((otherPayment) => {
            if (!isRenewalPayment(otherPayment)) {
                return false;
            }

            // Don't compare the payment with itself
            if (
                String(otherPayment?._id) === String(item?._id)
            ) {
                return false;
            }

            const otherPaymentDate = new Date(
                otherPayment?.createdAt
            );

            if (Number.isNaN(otherPaymentDate.getTime())) {
                return false;
            }

            // Renewal must have happened AFTER this payment
            if (otherPaymentDate <= currentPaymentDate) {
                return false;
            }

            // Does the later renewal reference this old booking?
            return (
                Array.isArray(otherPayment?.booking_id) &&
                otherPayment.booking_id.some(
                    (id) =>
                        String(id) === String(booking?._id)
                )
            );
        });
    };

    // Final status to display in history
    const getEnrollmentDisplayStatus = (item, booking) => {
        // New renewal -> use actual new booking status
        if (isNewRenewalPayment(item)) {
            return booking?.status === true;
        }

        // Old payment whose booking was reused by a later renewal
        if (wasRenewedLater(item, booking)) {
            return false;
        }

        return booking?.status === true;
    };

    // Which bookings should be displayed?
    const getEnrollmentBookingsForDisplay = (item) => {
        const bookings = item?.bookings_data || [];

        // NEW renewal:
        // Show the newly-created renewal bookings based on when
        // they were created, NOT their current status.
        //
        // This is important because a booking may later become
        // inactive due to transfer, but it must remain visible
        // in payment history.
        if (isNewRenewalPayment(item)) {
            const paymentDate = new Date(item?.createdAt);

            const renewalBookings = bookings.filter((booking) => {
                const bookingCreatedAt = new Date(booking?.createdAt);

                if (
                    Number.isNaN(paymentDate.getTime()) ||
                    Number.isNaN(bookingCreatedAt.getTime())
                ) {
                    return false;
                }

                return bookingCreatedAt >= paymentDate;
            });

            return renewalBookings.length > 0
                ? renewalBookings
                : bookings.filter((booking) => booking?.status === true);
        }

        // Existing behavior for normal/old payments
        return bookings;
    };

    

    const isEventPayment = (item) => {
        return item?.remarks?.startsWith("Payment for event booking");
    };

    const getMemberData = (item) => {
        if (Array.isArray(item?.member_data)) {
            return item.member_data[0] || {};
        }

        return item?.member_data || {};
    };

    const getPaymentMember = (item) => {
        const memberData = getMemberData(item);

        // Secondary member payment
        if (item?.secondary_member_id) {
            const familyDetails = memberData?.family_details || [];

            const secondaryMember = familyDetails.find((member) =>
                String(member?._id) === String(item.secondary_member_id) ||
                String(member?.id) === String(item.secondary_member_id) ||
                String(member?.secondary_member_id) === String(item.secondary_member_id)
            );

            if (secondaryMember) {
                return {
                    type: "Secondary Member",
                    id:
                        secondaryMember?.id ||
                        secondaryMember?.secondary_member_id ||
                        secondaryMember?._id ||
                        "-",
                    name: secondaryMember?.name || "-",
                    phone: secondaryMember?.mobile || secondaryMember?.phone || "-",
                };
            }
        }

        // Primary member payment
        return {
            type: "Primary Member",
            id: memberData?.member_id || "-",
            name: memberData?.name || "-",
            phone: memberData?.mobile || memberData?.phone || "-",
        };
    };

    const getActualMembershipAmount = (item) => {
        const hasMembership = item?.plans_data?.some(
            (plan) => plan?.plan_type === "membership"
        );

        if (!hasMembership) {
            return 0;
        }

        // If backend has an exact historical breakup, always use it.
        if (
            Array.isArray(item?.membership_breakdown) &&
            item.membership_breakdown.length > 0
        ) {
            return item.membership_breakdown.reduce(
                (total, member) => total + Number(member?.amount || 0),
                0
            );
        }

        // Get enrollment amount belonging to this payment.
        const bookings = getEnrollmentBookingsForDisplay(item);

        const enrollmentAmount = bookings.reduce((total, booking) => {
            return total + Number(booking?.total_amount || 0);
        }, 0);

        // Get the actual amount paid for this payment.
        const totalPaid = item?.is_transfer_combined
            ? Number(item?.combined_total_paid || 0)
            : getActualPaidAmount(item);

        // Both Membership + Enrollment
        if (bookings.length > 0) {
            return Math.max(totalPaid - enrollmentAmount, 0);
        }

        // Membership-only payment
        return Math.max(totalPaid, 0);
    };

    const getMembershipBreakdown = (item) => {
        const memberData = getMemberData(item);

        // ---------------------------------------------------------
        // 1. Exact backend historical breakup
        // ---------------------------------------------------------
        if (
            Array.isArray(item?.membership_breakdown) &&
            item.membership_breakdown.length > 0
        ) {
            return item.membership_breakdown.map((member) => {
                const isPrimary =
                    member?.member_type === "Primary Member" ||
                    member?.type === "Primary Member";

                const memberId =
                    member?.member_id && member.member_id !== "-"
                        ? member.member_id
                        : member?.id && member.id !== "-"
                        ? member.id
                        : isPrimary
                        ? memberData?.member_id || "-"
                        : "-";

                return {
                    type: member?.member_type || member?.type || "Member",
                    id: memberId,
                    name: member?.name || "-",
                    amount: Number(member?.amount || 0),
                };
            });
        }

        // ---------------------------------------------------------
        // 2. Membership plan
        // ---------------------------------------------------------
        const membershipPlan = item?.plans_data?.find(
            (plan) => plan?.plan_type === "membership"
        );

        if (!membershipPlan) {
            return [];
        }

        const actualMembershipAmount = getActualMembershipAmount(item);

        if (actualMembershipAmount <= 0) {
            return [];
        }

        const familyDetails = Array.isArray(memberData?.family_details)
            ? memberData.family_details
            : [];

        // ---------------------------------------------------------
        // 3. Secondary-member specific payment
        // ---------------------------------------------------------
        if (item?.secondary_member_id) {
            const secondaryMember = familyDetails.find(
                (member) =>
                    String(member?._id) === String(item.secondary_member_id) ||
                    String(member?.id) === String(item.secondary_member_id) ||
                    String(member?.secondary_member_id) ===
                        String(item.secondary_member_id)
            );

            if (secondaryMember) {
                return [
                    {
                        type: "Secondary Member",
                        id:
                            secondaryMember?.id ||
                            secondaryMember?.secondary_member_id ||
                            secondaryMember?._id ||
                            "-",
                        name: secondaryMember?.name || "-",
                        amount: actualMembershipAmount,
                    },
                ];
            }
        }

        // ---------------------------------------------------------
        // 4. Primary member object
        // ---------------------------------------------------------
        const primaryMember = {
            type: "Primary Member",
            id: memberData?.member_id || "-",
            name: memberData?.name || "-",
            isPrimary: true,
            is_dependent: false,
            plans: membershipPlan,
        };

        // ---------------------------------------------------------
        // 5. Determine WHO was included in this payment
        //
        // For combined enrollment + membership payments,
        // booking.family_member tells us who the enrollment
        // belongs to.
        //
        // June:
        // family_member = [null] -> Primary
        //
        // August:
        // booking 1 -> S1
        // booking 2 -> S2
        // ---------------------------------------------------------
        const bookings = Array.isArray(item?.bookings_data)
            ? item.bookings_data
            : [];

        const paymentMembers = [];

        if (bookings.length > 0) {
            bookings.forEach((booking) => {
                const familyRefs = Array.isArray(booking?.family_member)
                    ? booking.family_member
                    : [];

                // Empty family_member means primary member
                if (familyRefs.length === 0) {
                    paymentMembers.push(primaryMember);
                    return;
                }

                familyRefs.forEach((fam) => {
                    // null means primary member
                    if (!fam) {
                        paymentMembers.push(primaryMember);
                        return;
                    }

                    // family_member is already an object
                    if (
                        typeof fam === "object" &&
                        (fam?.name ||
                            fam?.id ||
                            fam?._id ||
                            fam?.secondary_member_id)
                    ) {
                        const familyId =
                            fam?.id ||
                            fam?.secondary_member_id ||
                            fam?._id ||
                            "-";

                        const foundFamily = familyDetails.find(
                            (family) =>
                                String(family?.id) === String(familyId) ||
                                String(family?._id) === String(familyId) ||
                                String(family?.secondary_member_id) ===
                                    String(familyId)
                        );

                        paymentMembers.push({
                            type: "Secondary Member",
                            id: familyId,
                            name:
                                fam?.name ||
                                foundFamily?.name ||
                                "-",
                            isPrimary: false,
                            is_dependent:
                                fam?.is_dependent ??
                                foundFamily?.is_dependent ??
                                false,
                            plans:
                                fam?.plans ||
                                foundFamily?.plans ||
                                membershipPlan,
                        });

                        return;
                    }

                    // family_member is an ID/reference
                    const familyId = fam?.toString?.();

                    if (!familyId) {
                        return;
                    }

                    const foundFamily = familyDetails.find(
                        (family) =>
                            String(family?._id) === String(familyId) ||
                            String(family?.id) === String(familyId) ||
                            String(family?.secondary_member_id) ===
                                String(familyId)
                    );

                    if (foundFamily) {
                        paymentMembers.push({
                            type: "Secondary Member",
                            id:
                                foundFamily?.id ||
                                foundFamily?.secondary_member_id ||
                                foundFamily?._id ||
                                "-",
                            name: foundFamily?.name || "-",
                            isPrimary: false,
                            is_dependent: foundFamily?.is_dependent || false,
                            plans:
                                foundFamily?.plans ||
                                membershipPlan,
                        });
                    }
                });
            });
        }

        // ---------------------------------------------------------
        // 6. Membership-only payment fallback
        //
        // If there are no bookings, use the historical amount
        // and then allocate it against the available members.
        // ---------------------------------------------------------
        if (paymentMembers.length === 0) {
            paymentMembers.push(primaryMember);

            familyDetails.forEach((family) => {
                paymentMembers.push({
                    type: "Secondary Member",
                    id:
                        family?.id ||
                        family?.secondary_member_id ||
                        family?._id ||
                        "-",
                    name: family?.name || "-",
                    isPrimary: false,
                    is_dependent: family?.is_dependent || false,
                    plans:
                        family?.plans ||
                        membershipPlan,
                });
            });
        }

        // ---------------------------------------------------------
        // 7. Remove duplicate members
        // ---------------------------------------------------------
        const uniqueMembers = Array.from(
            new Map(
                paymentMembers.map((member) => [
                    `${member.type}-${String(member.id)}`,
                    member,
                ])
            ).values()
        );

        // ---------------------------------------------------------
        // 8. Allocate the ACTUAL membership amount
        // ---------------------------------------------------------
        let remainingAmount = actualMembershipAmount;
        const breakdown = [];

        for (const member of uniqueMembers) {
            if (remainingAmount <= 0) {
                break;
            }

            let memberAmount = 0;

            if (member.isPrimary) {
                memberAmount = Number(membershipPlan?.amount || 0);
            } else {
                const isDependent =
                    member?.is_dependent === true ||
                    member?.is_dependent === "true";

                memberAmount = isDependent
                    ? Number(
                        member?.plans?.dependent_member_price ??
                            membershipPlan?.dependent_member_price ??
                            0
                    )
                    : Number(
                        member?.plans?.non_dependent_member_price ??
                            membershipPlan?.non_dependent_member_price ??
                            0
                    );
            }

            if (memberAmount <= 0) {
                continue;
            }

            // Never show more membership amount than was actually paid
            const amountToShow = Math.min(
                memberAmount,
                remainingAmount
            );

            if (amountToShow > 0) {
                breakdown.push({
                    type: member.type,
                    id: member.id,
                    name: member.name,
                    amount: amountToShow,
                });

                remainingAmount -= amountToShow;
            }
        }

        return breakdown;
    };



    const getEventMember = (item) => {
        const memberId =
            item?.remarks?.match(/member id\s*:\s*([^,\s]+)/i)?.[1]?.trim();

        if (!memberId) {
            return {
                type: "Primary Member",
                id: item?.member_data?.member_id || "-",
                name: item?.member_data?.name || "-",
            };
        }

        if (
            item?.member_data?.member_id?.toString() ===
            memberId.toString()
        ) {
            return {
                type: "Primary Member",
                id: item.member_data.member_id,
                name: item.member_data.name || "-",
            };
        }

        const familyMember = item?.member_data?.family_details?.find(
            (member) =>
                member?.id?.toString() === memberId.toString() ||
                member?.secondary_member_id?.toString() === memberId.toString() ||
                member?._id?.toString() === memberId.toString()
        );

        if (familyMember) {
            return {
                type: "Secondary Member",
                id:
                    familyMember.id ||
                    familyMember.secondary_member_id ||
                    familyMember._id ||
                    "-",
                name: familyMember.name || "-",
            };
        }

        return {
            type: "Member",
            id: memberId,
            name: "-",
        };
    };

    const getBookingMembers = (item, booking) => {
        const primaryMember = {
            type: "Primary Member",
            id: item?.member_data?.member_id || "-",
            name: item?.member_data?.name || "-",
        };

        const familyDetails = item?.member_data?.family_details || [];
        const familyRefs = Array.isArray(booking?.family_member)
            ? booking.family_member
            : [];

        // No secondary member specified = booking is for primary member
        if (familyRefs.length === 0) {
            return [primaryMember];
        }

        const secondaryMembers = familyRefs
            .map((fam) => {
                // family_member is already an object
                if (
                    fam &&
                    typeof fam === "object" &&
                    (fam.name || fam.id || fam._id || fam.secondary_member_id)
                ) {
                    return {
                        type: "Secondary Member",
                        id:
                            fam.id ||
                            fam.secondary_member_id ||
                            fam._id ||
                            "-",
                        name: fam.name || "-",
                    };
                }

                // family_member is an ID/reference
                const famStr = fam?.toString?.();

                const found = familyDetails.find((fd) => {
                    if (!fd || !famStr) return false;

                    return (
                        (fd._id && fd._id.toString() === famStr) ||
                        (fd.id && fd.id.toString() === famStr) ||
                        (fd.secondary_member_id &&
                            fd.secondary_member_id.toString() === famStr)
                    );
                });

                if (found) {
                    return {
                        type: "Secondary Member",
                        id:
                            found.id ||
                            found.secondary_member_id ||
                            found._id?.toString() ||
                            "-",
                        name: found.name || "-",
                    };
                }

                return null;
            })
            .filter(Boolean);

        return secondaryMembers.length > 0
            ? secondaryMembers
            : [primaryMember];
    };

    const isEnrollmentItem = (item) => {
        return (
            item?.plans_data?.some(
                (plan) => plan?.plan_type === "enrollment"
            ) ||
            item?.bookings_data?.some(
                (booking) =>
                    booking?.type === "enrollment" ||
                    booking?.fees_breakup?.plan_type === "enrollment"
            )
        );
    };

    const getTypeLabel = (item) => {
        const plans = item?.plans_data || [];

        const hasMembership = plans.some(
            (p) => p?.plan_type === "membership"
        );

        const hasEnrollment = isEnrollmentItem(item);

        const hasEvent = isEventPayment(item);

        if (hasMembership && hasEnrollment) return "Both";
        if (hasMembership) return "Membership";
        if (hasEnrollment) return "Enrollment";
        if (hasEvent) return "Events";
        if (item?.booking_type === "booking") return "Booking";

        return "Unknown";
    };

    const updateRemarkAPI = async (id, data) => {
        try {
            const response = await fetch(`${baseUrl}/payment/update-remark`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getCookie("asmc_token")}`,
                },
                body: JSON.stringify({ _id: id, ...data }),
            });
            if (response.ok) {
                await response.json();
                refetch();
                setEditingIndex(null);
            }
        } catch (error) {
            console.error(error);
        }
    };

    if (isLoading || isFetching) {
        return (
            <Dialog open={Boolean(show)} onClose={close} maxWidth="md">
                <DialogTitle>Loading...</DialogTitle>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={Boolean(show)}
            onClose={() => {
                close();
                setFilteredData([]);
            }}
            maxWidth="lg"
            fullWidth
            sx={{ zIndex: 1200 }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">Payment History</Typography>
                    <Typography variant="subtitle2">
                        Total Membership Amount: Rs. {getTotalAmount("membership") || 0}
                    </Typography>
                    <Typography variant="subtitle2">
                        Total Enrollment Amount: Rs. {getTotalAmount("enrollment") || 0}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Paper sx={{ mb: 2 }}>
                    <Typography variant="h6">Filter by Year</Typography>
                    <Stack direction="row" spacing={1}>
                        {years.map((year, key) => {
                            return (
                                <Chip
                                    key={key}
                                    label={year}
                                    onClick={() => {
                                        setYearFilter(year);
                                    }}
                                    variant={yearFilter === year ? "filled" : "outlined"}
                                    color={"primary"}
                                />
                            );
                        })}
                    </Stack>
                </Paper>
                <Paper sx={{ mb: 2}}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Filter by Type
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {["All", "Membership", "Enrollment", "Events", "Booking", "Both"].map(
                            (type) => (
                                <Chip
                                    key={type}
                                    label={type}
                                    onClick={() => {
                                        setPaymentTypeFilter(type);
                                    }}
                                    variant={
                                        paymentTypeFilter === type
                                            ? "filled"
                                            : "outlined"
                                    }
                                    color="primary"
                                />
                            )
                        )}
                    </Stack>
                </Paper>
                {!filteredData || filteredData?.length === 0 ? (
                    <Typography>No history available</Typography>
                ) : (
                    filteredData.map((item, index) => {
                        const paymentRes = JsonDecode(item.payment_response);
                        let isSecondary = item.bookings_data?.[0]?.family_member?.[0];

                        if (item?.secondary_member_id) {
                            isSecondary = item?.member_data?.family_details.find(
                                (member) => member._id === item?.secondary_member_id,
                            );
                        }
                        return (
                            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, borderLeft: "5px solid #1976d2" }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="h6">
                                                #{index + 1} —{" "}
                                                <Chip label={getTypeLabel(item)} size="medium" color="primary">
                                                    {getTypeLabel(item)}
                                                </Chip>
                                            </Typography>
                                        </Stack>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>

                                    {/* Member Info: show primary + any secondary/non-member players included in this payment */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            <strong>Members in this Payment</strong>
                                        </Typography>
                                        {/* Primary Member */}
                                        {(() => {
                                            const paymentMember = getPaymentMember(item);

                                            return (
                                                <Box
                                                    mb={1}
                                                    p={1}
                                                    border={1}
                                                    borderColor="grey.200"
                                                    borderRadius={1}
                                                >
                                                    <Typography>
                                                        <strong>{paymentMember.type}</strong>
                                                    </Typography>

                                                    <Typography>
                                                        <strong>ID:</strong> {paymentMember.id}
                                                    </Typography>

                                                    <Typography>
                                                        <strong>Name:</strong> {paymentMember.name}
                                                    </Typography>

                                                    <Typography>
                                                        <strong>Phone:</strong> {paymentMember.phone}
                                                    </Typography>
                                                </Box>
                                            );
                                        })()}

                                        {/* Derived players / secondary members from bookings_data */}
                                        {getEnrollmentBookingsForDisplay(item)?.map((booking, bi) => {
                                            // players can be non-member players or family_member references
                                            const players = booking.players || [];
                                            const familyRefs = Array.isArray(booking?.family_member)
                                                ? booking.family_member.filter(Boolean)
                                                : [];

                                            const resolveFamilyMember = (fam) => {
                                                // If fam is an object with a name, return it directly
                                                if (fam && typeof fam === "object" && (fam.name || fam.id || fam._id || fam.secondary_member_id)) {
                                                    return {
                                                        name: fam.name || fam.name === "" ? fam.name : null,
                                                        id: fam.id || fam.secondary_member_id || fam._id || null,
                                                    };
                                                }

                                                // Otherwise try to resolve against member_data.family_details (which may contain objects with _id and id)
                                                const familyDetails = item.member_data?.family_details || [];
                                                if (familyDetails && familyDetails.length > 0) {
                                                    const found = familyDetails.find((fd) => {
                                                        try {
                                                            if (!fd) return false;
                                                            if (fam && typeof fam === "object" && fam._id) {
                                                                return fd._id && fd._id.toString() === fam._id.toString();
                                                            }
                                                            // fam might be a plain id string (like 'S1-00001') or ObjectId string
                                                            const famStr = fam && fam.toString ? fam.toString() : null;
                                                            if (!famStr) return false;
                                                            if (fd._id && fd._id.toString() === famStr) return true;
                                                            if (fd.id && fd.id.toString() === famStr) return true;
                                                            if (fd.secondary_member_id && fd.secondary_member_id.toString() === famStr) return true;
                                                            return false;
                                                        } catch (e) {
                                                            return false;
                                                        }
                                                    });
                                                    if (found) {
                                                        return { name: found.name, id: found.id || found.secondary_member_id || (found._id && found._id.toString()) };
                                                    }
                                                }

                                                return null;
                                            };

                                            return (
                                                <Box key={bi} mb={1} p={1} border={1} borderColor="grey.100" borderRadius={1}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Booking #{booking.booking_id} — Amount: Rs. {getEnrollmentBookingAmount(booking)}
                                                    </Typography>
                                                    {familyRefs.length > 0 &&
                                                        familyRefs.map((fam, fi) => {
                                                            const fm = resolveFamilyMember(fam);
                                                            const name = fm ? fm.name || "Secondary Member" : (fam && fam.name) || "Secondary Member";
                                                            const memberId = fm ? fm.id || "-" : (fam && (fam.id || fam.secondary_member_id)) || "-";
                                                            return (
                                                                <Box key={fi} sx={{ ml: 1, mb: 0.5 }}>
                                                                    <Typography>
                                                                        <strong>Secondary:</strong> {name} ({memberId})
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}

                                                    {players.length > 0 &&
                                                        players.map((p, pi) => (
                                                            <Box key={pi} sx={{ ml: 1, mb: 0.5 }}>
                                                                <Typography>
                                                                    <strong>Player:</strong> {p.name || p.email || p.mobile} — CHSS: {p.chss_number || "-"}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                </Box>
                                            );
                                        })}
                                    </Grid>

                                    {/* Booking Details */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Booking & Activity</Typography>
                                        {isEventPayment(item) && (
                                            <Box
                                                mb={2}
                                                p={1}
                                                border={1}
                                                borderColor="grey.300"
                                                borderRadius={2}
                                                bgcolor="#e8f5e9"
                                            >
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Event Booking - Successful
                                                </Typography>

                                                {(() => {
                                                    const eventMember = getEventMember(item);

                                                    return (
                                                        <>
                                                            <Typography>
                                                                <strong>Booked For:</strong> {eventMember.type}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>ID:</strong> {eventMember.id}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>Name:</strong> {eventMember.name}
                                                            </Typography>
                                                        </>
                                                    );
                                                })()}

                                                <Typography>
                                                    <strong>Booking ID:</strong>{" "}
                                                    {item?.remarks?.match(/Booking Id:\s*([^,]+)/i)?.[1] || "-"}
                                                </Typography>

                                                <Typography>
                                                    <strong>Event:</strong>{" "}
                                                    {item?.remarks
                                                        ?.match(
                                                            /Event Name\s*:\s*([^,]+?)(?=,\s*Event Id|$)/i
                                                        )?.[1] || "-"}
                                                </Typography>

                                                <Typography>
                                                    <strong>Event ID:</strong>{" "}
                                                    {item?.remarks?.match(/Event Id\s*:\s*([^,]+)/i)?.[1] || "-"}
                                                </Typography>

                                                <Typography>
                                                    <strong>Amount:</strong> Rs. {item?.amount_paid || 0}
                                                </Typography>
                                            </Box>
                                        )}
                                        {item?.plans_data?.some(
                                            (plan) => plan?.plan_type === "membership"
                                        ) && (
                                            <Box
                                                mb={2}
                                                p={1}
                                                border={1}
                                                borderColor="grey.300"
                                                borderRadius={2}
                                                bgcolor="#e8f5e9"
                                            >
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Membership - Active
                                                </Typography>

                                                {getMembershipBreakdown(item).map((member, mi) => (
                                                    <Box
                                                        key={mi}
                                                        mb={1}
                                                        p={1}
                                                        border={1}
                                                        borderColor="grey.200"
                                                        borderRadius={1}
                                                        bgcolor="#f5f5f5"
                                                    >
                                                        <Typography>
                                                            <strong>Booked For:</strong>{" "}
                                                            {member.type}
                                                        </Typography>

                                                        <Typography>
                                                            <strong>ID:</strong>{" "}
                                                            {member.id}
                                                        </Typography>

                                                        <Typography>
                                                            <strong>Name:</strong>{" "}
                                                            {member.name}
                                                        </Typography>

                                                        <Typography>
                                                            <strong>Amount:</strong>{" "}
                                                            Rs. {member.amount}
                                                        </Typography>
                                                    </Box>
                                                ))}

                                                {item?.plans_data
                                                    ?.filter(
                                                        (plan) => plan?.plan_type === "membership"
                                                    )
                                                    ?.map((plan, pi) => (
                                                        <React.Fragment key={pi}>
                                                            <Typography>
                                                                <strong>Plan Name:</strong>{" "}
                                                                {plan?.plan_name || "-"}
                                                            </Typography>
                                                        </React.Fragment>
                                                    ))}
                                            </Box>
                                        )}
                                        {getEnrollmentBookingsForDisplay(item)?.map((booking, i) => {
                                            const displayStatus = getEnrollmentDisplayStatus(item, booking);
                                            const displayPlan = getEnrollmentPlanForDisplay(item, booking);
                                            return (
                                                <Box
                                                    key={i}
                                                    mb={2}
                                                    p={1}
                                                    border={1}
                                                    borderColor="grey.300"
                                                    borderRadius={2}
                                                    bgcolor={displayStatus ? "#e8f5e9" : "#ff756b"}
                                                >
                                                    <Typography variant="subtitle2" gutterBottom>
                                                    {booking?.type === "booking" && !isEnrollmentItem(item)
                                                        ? "Booking"
                                                        : "Enrollment"}{" "}
                                                    {i + 1} -{" "}
                                                    {displayStatus ? "Active" : "Inactive"}
                                                    </Typography>

                                                    {getBookingMembers(item, booking).map((member, mi) => (
                                                        <Box
                                                            key={mi}
                                                            mb={1}
                                                            p={1}
                                                            border={1}
                                                            borderColor="grey.200"
                                                            borderRadius={1}
                                                            bgcolor="#f5f5f5"
                                                        >
                                                            <Typography>
                                                                <strong>Booked For:</strong> {member.type}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>ID:</strong> {member.id}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>Name:</strong> {member.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}

                                                    <Typography>
                                                        <strong>
                                                            {booking?.type === "booking" && !isEnrollmentItem(item)
                                                                ? "Booking ID"
                                                                : "Enroll ID"}:
                                                        </strong>{" "}
                                                        {booking.booking_id}{" "}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setBookingType(
                                                                    booking?.type === "booking" && !isEnrollmentItem(item)
                                                                        ? "booking"
                                                                        : "enrollment"
                                                                );
                                                                setBookingId(booking.booking_id);
                                                            }}
                                                            loading={bookingsLoading}
                                                            fullWidth={false}
                                                        >
                                                            View
                                                        </Button>
                                                    </Typography>

                                                    {(() => {
                                                        const activity = item?.activity_data?.find(
                                                            (activity) =>
                                                                String(activity?._id) === String(booking?.activity_id)
                                                        );

                                                        return activity ? (
                                                            <Typography>
                                                                <strong>Activity:</strong>{" "}
                                                                {activity.name} ({activity.activity_id})
                                                            </Typography>
                                                        ) : null;
                                                    })()}

                                                    <Typography>
                                                        <strong>Plan Name:</strong>{" "}
                                                        {displayPlan?.plan_name || "-"}
                                                    </Typography>

                                                    <Typography>
                                                        <strong>Amount:</strong>{" "}
                                                        Rs. {
                                                            item?.booking_type === "booking"
                                                                ? getSingleAmount(item, "booking")
                                                                : getEnrollmentBookingAmount(booking)
                                                        }
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Grid>

                                    {/* Payment Info */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Payment Info</Typography>
                                        <Typography>
                                            <strong>Membership Amount:</strong> Rs. {getSingleAmount(item, "membership")}
                                        </Typography>
                                        <Typography>
                                            <Typography>
                                                <strong>
                                                    {item?.booking_type === "booking"
                                                        ? "Booking Amount"
                                                        : "Enrollment Amount"}:
                                                </strong>{" "}
                                                Rs. {
                                                    item?.booking_type === "booking"
                                                        ? getSingleAmount(item, "booking")
                                                        : getItemEnrollmentAmount(item)
                                                }
                                            </Typography>
                                        </Typography>
                                                {Number(item?.wallet_amount_used || 0) > 0 &&
                                                (() => {
                                                    const isBookingOrEvent =
                                                        item?.booking_type === "booking" ||
                                                        isEventPayment(item);

                                                    const isMembershipOnly =
                                                        item?.plans_data?.some(
                                                            (plan) => plan?.plan_type === "membership"
                                                        ) &&
                                                        !isEnrollmentItem(item) &&
                                                        item?.booking_type !== "booking";

                                                    const {
                                                        walletAmount,
                                                        onlineAmount,
                                                    } = isMembershipOnly
                                                        ? getMembershipPaymentBreakdown(item)
                                                        : isBookingOrEvent
                                                        ? getBookingEventPaymentBreakdown(item)
                                                        : getEnrollmentPaymentBreakdown(item);

                                                    return (
                                                        <>
                                                            <Typography>
                                                                <strong>Wallet Amount:</strong> Rs. {walletAmount}
                                                            </Typography>

                                                            <Typography>
                                                                <strong>Online Amount:</strong> Rs. {onlineAmount}
                                                            </Typography>
                                                        </>
                                                    );
                                                })()}
                                        {item?.is_transfer_combined && (
                                            <Typography>
                                                <strong>Payment Breakdown:</strong>{" "}
                                                {item.combined_payment_parts
                                                    ?.map(
                                                        (payment) =>
                                                            `${payment.mode} ₹${payment.amount}`
                                                    )
                                                    .join(" + ")}
                                            </Typography>
                                        )}

                                        <Typography color={"error"}>
                                            <strong>
                                                Total Amount Paid: Rs.{" "}
                                                {item?.is_transfer_combined
                                                    ? item?.combined_total_paid
                                                    : getActualPaidAmount(item) || "-"}
                                            </strong>
                                        </Typography>

                                        <Typography>
                                        <strong>Mode:</strong>{" "}
                                        {item?.is_transfer_combined
                                            ? item.combined_payment_parts
                                                ?.map((payment) => `${payment.mode} ₹${payment.amount}`)
                                                .join(" + ")
                                            : Number(item?.wallet_amount_used || 0) > 0
                                            ? (() => {
                                                const {
                                                    walletAmount,
                                                    onlineAmount,
                                                } = item?.plans_data?.some(
                                                    (plan) => plan?.plan_type === "membership"
                                                ) && !isEnrollmentItem(item)
                                                    ? getMembershipPaymentBreakdown(item)
                                                    : item?.booking_type === "booking" || isEventPayment(item)
                                                    ? getBookingEventPaymentBreakdown(item)
                                                    : getEnrollmentPaymentBreakdown(item);

                                                return [
                                                    walletAmount > 0 ? `Wallet ₹${walletAmount}` : null,
                                                    onlineAmount > 0 ? `Online ₹${onlineAmount}` : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" + ");
                                            })()
                                            : item?.payment_mode}
                                    </Typography>
                                        <Typography>
                                            <strong>Order ID:</strong> {item?.order_id || "-"}
                                        </Typography>
                                        <Typography>
                                            <strong>Tracking ID:</strong> {paymentRes?.tracking_id || "-"}
                                        </Typography>
                                        <Typography>
                                            <strong>Date:</strong> {handleDateTimeDefault(item.createdAt)}
                                        </Typography>
                                        <Typography>
                                            <strong>Remarks:</strong>{" "}
                                            {/* {editingIndex === index ? (
                                                <Box>
                                                    <textarea
                                                        value={editedRemark}
                                                        onChange={(e) => setEditedRemark(e.target.value)}
                                                        rows={4}
                                                        style={{
                                                            width: "100%",
                                                            marginRight: "8px",
                                                            marginBottom: "8px",
                                                            padding: "8px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc",
                                                            fontFamily: "inherit",
                                                            fontSize: "14px",
                                                            resize: "vertical",
                                                        }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth={false}
                                                        onClick={async () => {
                                                            try {
                                                                await updateRemarkAPI(item._id, editedRemark); // replace with your actual API call
                                                                item.remarks = editedRemark; // optimistically update UI
                                                                setEditingIndex(null);
                                                            } catch (err) {
                                                                console.error("Failed to update remarks", err);
                                                            }
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        color="error"
                                                        fullWidth={false}
                                                        onClick={() => setEditingIndex(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            ) : ( */}
                                            {/* <Button
                                                        size="small"
                                                        fullWidth={false}
                                                        variant="contained"
                                                        onClick={() => {
                                                            setEditingIndex(index);
                                                            setEditedRemark(item?.remarks || "");
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>{" "} */}
                                            {item?.remarks || "-"}
                                        </Typography>

                                        {editingIndex === index && (
                                            <Grid container spacing={2} sx={{ mt: 2, mb: 2 }} alignItems="center">
                                                <Grid item xs={12} sm={12}>
                                                    <textarea
                                                        value={editedRemark || item?.remarks || ""}
                                                        onChange={(e) => setEditedRemark(e.target.value)}
                                                        rows={4}
                                                        style={{
                                                            width: "100%",
                                                            padding: "8px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc",
                                                            fontFamily: "inherit",
                                                            fontSize: "14px",
                                                            resize: "vertical",
                                                        }}
                                                        placeholder="Enter remarks"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <Input
                                                        id="difference_amount_paid"
                                                        name="difference_amount_paid"
                                                        label="Difference Amount Paid"
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value, 10);
                                                            setEditDifferenceAmount(
                                                                isNaN(value) || value < 0 ? null : value,
                                                            );
                                                        }}
                                                        value={differenceAmount || ""}
                                                        fullWidth
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        onClick={async () => {
                                                            try {
                                                                await updateRemarkAPI(item._id, {
                                                                    remarks: editedRemark,
                                                                    difference_amount_paid: differenceAmount,
                                                                }); // replace with your actual API call
                                                                item.remarks = editedRemark;
                                                                item.difference_amount_paid = differenceAmount;
                                                                setEditingIndex(null);
                                                            } catch (err) {
                                                                console.error(
                                                                    "Failed to update difference amount",
                                                                    err,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        color="error"
                                                        fullWidth
                                                        onClick={() => {
                                                            setEditingIndex(null);
                                                            setEditedRemark("");
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        )}

                                        <Button
                                            size="small"
                                            fullWidth={false}
                                            variant="contained"
                                            onClick={() => {
                                                setEditingIndex(index);
                                            }}
                                        >
                                            Add Difference Amount
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })
                )}
            </DialogContent>

            <EnrollActivityAddDrawer
                type="enrollment"
                show={showDrawer}
                close={() => [setShowDrawer(false), setBookingId(null), setBookingInitalValue(null), refetch()]}
                formType={isAuth().roles === "super" ? "Edit" : "View"}
                initialValues={bookingInitalValue}
                getActivityList={getActivityList}
                getMembersList={getMembersList}
            />
        </Dialog>
    );
};
